import React from "react";

import type {
  CompanyResearchAvailableAction,
  CompanyResearchIntentType,
  CompanyResearchJobDetail,
  CompanyResearchJobStatus,
} from "../lib/companyResearch";

const ACTION_DESCRIPTIONS: Record<CompanyResearchIntentType, string> = {
  learn_more: "公開情報をさらに調べ、目的別分析の土台となる企業情報を整理します。",
  sales: "営業提案の切り口や、初回ヒアリング項目を整理します。",
  partnership: "パートナー候補として接点や協業テーマを整理します。",
  risk_check: "提案前の懸念点や確認事項を整理します。",
};

const NON_TERMINAL_STATUSES: CompanyResearchJobStatus[] = [
  "accepted",
  "queued",
  "pending",
  "running",
  "processing",
];

function jobTime(job: CompanyResearchJobDetail) {
  const value = job.completed_at || job.updated_at || job.created_at;
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function getLatestCompletedCompanyResearchJob(
  jobs: CompanyResearchJobDetail[],
  intentType: CompanyResearchIntentType
) {
  return jobs
    .filter((job) => job.intent_type === intentType && job.status === "completed")
    .sort((a, b) => jobTime(b) - jobTime(a))[0] ?? null;
}

export function hasRunningCompanyResearchJob(
  jobs: CompanyResearchJobDetail[],
  intentType: CompanyResearchIntentType
) {
  return jobs.some(
    (job) => job.intent_type === intentType && NON_TERMINAL_STATUSES.includes(job.status)
  );
}

function formatCompletedAt(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

type CompanyResearchActionCardsProps = {
  actions: CompanyResearchAvailableAction[];
  jobs?: CompanyResearchJobDetail[];
  loadingAction?: CompanyResearchIntentType | null;
  optimisticPendingIntentTypes?: CompanyResearchIntentType[];
  disabled?: boolean;
  onRequestAnalysis: (intentType: CompanyResearchIntentType, isReanalysis: boolean) => void;
  onViewResult?: (job: CompanyResearchJobDetail) => void;
};

export function CompanyResearchActionCards({
  actions,
  jobs = [],
  loadingAction = null,
  optimisticPendingIntentTypes = [],
  disabled = false,
  onRequestAnalysis,
  onViewResult,
}: CompanyResearchActionCardsProps) {
  const learnMoreCompleted = Boolean(getLatestCompletedCompanyResearchJob(jobs, "learn_more"));

  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {actions.map((action) => {
        const intentType = action.action_type;
        const latestCompleted = getLatestCompletedCompanyResearchJob(jobs, intentType);
        const isStarting = loadingAction === intentType;
        const isRunning = hasRunningCompanyResearchJob(jobs, intentType)
          || optimisticPendingIntentTypes.includes(intentType);
        const isUnavailable = action.enabled === false || action.status === "coming_soon" || action.status === "disabled";
        const isRecommended = intentType === "learn_more" && !learnMoreCompleted && !isRunning;
        const isDisabled = disabled || loadingAction !== null || isRunning || isUnavailable;
        const completedAt = formatCompletedAt(latestCompleted?.completed_at || latestCompleted?.updated_at);

        return (
          <article
            key={intentType}
            className={`flex min-h-[176px] flex-col justify-between rounded-2xl border p-4 shadow-sm ${
              isRecommended
                ? "border-[#A67C52] bg-[#7C5A4E] text-white shadow-[0_12px_26px_rgba(124,90,78,0.20)]"
                : "border-[#EADBC8] bg-white"
            }`}
          >
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className={`text-sm font-black leading-6 ${isRecommended ? "text-white" : "text-[#5D4D46]"}`}>
                  {action.label}
                </h3>
                {isRecommended ? (
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-[#7C5A4E]">おすすめ</span>
                ) : latestCompleted ? (
                  <span className="rounded-full bg-[#F4FAF1] px-2 py-0.5 text-[10px] font-black text-[#4D6A48]">
                    ✓ {intentType === "learn_more" ? "調査済み" : "分析済み"}
                  </span>
                ) : null}
              </div>
              <p className={`mt-2 text-xs font-bold leading-5 ${isRecommended ? "text-white/90" : "text-[#6B5851]"}`}>
                {action.description || ACTION_DESCRIPTIONS[intentType]}
              </p>
              {completedAt ? (
                <p className={`mt-2 text-[11px] font-bold ${isRecommended ? "text-white/80" : "text-[#8A746A]"}`}>
                  {intentType === "learn_more" ? "調査済み" : "分析済み"} {completedAt}
                </p>
              ) : null}
            </div>

            <div className="mt-4 grid gap-2">
              <button
                type="button"
                onClick={() => {
                  if (latestCompleted && onViewResult) {
                    onViewResult(latestCompleted);
                    return;
                  }
                  onRequestAnalysis(intentType, false);
                }}
                disabled={isDisabled}
                className={`min-h-[42px] rounded-full px-4 py-2 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  isRecommended
                    ? "bg-white text-[#7C5A4E] hover:bg-[#FFF7F2]"
                    : "bg-[#7C5A4E] text-white hover:bg-[#5D4D46]"
                }`}
              >
                {isStarting
                  ? "開始中..."
                  : isRunning
                    ? "分析中"
                    : latestCompleted
                      ? "結果を見る"
                      : isUnavailable
                        ? "準備中"
                        : action.label}
              </button>
              {latestCompleted && !isRunning ? (
                <button
                  type="button"
                  onClick={() => onRequestAnalysis(intentType, true)}
                  disabled={disabled || loadingAction !== null || isUnavailable}
                  className="min-h-[36px] rounded-full border border-[#D8C0AF] bg-white px-3 py-1.5 text-xs font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {intentType === "learn_more" ? "再調査" : "再分析"}
                </button>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}
